import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  subscribeToChat,
  unsubscribeFromChat,
  subscribeToInternalChat,
  unsubscribeFromInternalChat,
  publishTypingIndicator,
  publishMessageRead,
  initializeAbly,
  addConnectionListener,
  getConnectionState,
  setQueryClient,
} from "@/lib/ably";
import { useAuthStore } from "@/stores/authStore";

export const useRealtimeMessages = (
  chatId?: string,
  onNewMessage?: () => void,
  isInternal: boolean = false
) => {
  const { isAuthenticated } = useAuthStore();
  const [connectionState, setConnectionState] = useState(getConnectionState());
  const queryClient = useQueryClient();

  // Set the queryClient in Ably module on first use
  useEffect(() => {
    setQueryClient(queryClient);
  }, [queryClient]);

  // Monitor connection state
  useEffect(() => {
    const removeListener = addConnectionListener(setConnectionState);
    return () => {
      removeListener();
    };
  }, []);

  // Initialize Ably connection on mount
  useEffect(() => {
    if (isAuthenticated) {
      initializeAbly().catch((error) => {
        console.error("Failed to initialize Ably:", error);
      });
    }
  }, [isAuthenticated]);

  // Subscribe to chat when chatId changes and connection is ready
  useEffect(() => {
    if (!isAuthenticated || !chatId || !connectionState.isConnected) return;

    if (isInternal) {
      subscribeToInternalChat(chatId, "conversation", onNewMessage);
      // Cleanup on unmount or when chatId changes
      return () => {
        unsubscribeFromInternalChat(chatId, "conversation");
      };
    } else {
      subscribeToChat(chatId, "conversation", onNewMessage);
      // Cleanup on unmount or when chatId changes
      return () => {
        unsubscribeFromChat(chatId, "conversation");
      };
    }
  }, [
    chatId,
    isAuthenticated,
    connectionState.isConnected,
    onNewMessage,
    isInternal,
  ]);

  // Utility functions
  const sendTypingIndicator = (isTyping: boolean) => {
    if (chatId && connectionState.isConnected) {
      publishTypingIndicator(chatId, isTyping, isInternal);
    }
  };

  const markAsRead = () => {
    if (chatId && connectionState.isConnected) {
      publishMessageRead(chatId, isInternal);
    }
  };

  return {
    sendTypingIndicator,
    markAsRead,
    isConnected: connectionState.isConnected,
    connectionState,
  };
};
